// Database-backed notification service using global event system
import notificationApiService from './notificationApiService';

class NotificationService {
  constructor() {
    this.listeners = new Map();
    this.notifications = [];
    this.nextId = 1;
    this.isLoading = false;
    this.isInitialized = false;
    
    // Don't load notifications immediately - wait for first subscription
  }

  // Subscribe to notifications
  subscribe(callback) {
    const id = Date.now() + Math.random();
    this.listeners.set(id, callback);
    
    // Initialize on first subscription
    if (!this.isInitialized) {
      this.isInitialized = true;
      this.loadNotifications();
    }
    
    return () => {
      this.listeners.delete(id);
    };
  }

  // Send notification to all subscribers
  async sendNotification(title, message, type = 'info', metadata = null, permissionRequired = null) {
    try {
      // Create notification in database
      const response = await notificationApiService.createNotification({
        title,
        message,
        type,
        metadata,
        permission_required: permissionRequired
      });

      // Check if response has the expected structure
      if (!response || !response.data || !response.data.id) {
        throw new Error('Invalid API response structure');
      }

      const notification = {
        id: response.data.id,
        title: response.data.title,
        message: response.data.message,
        type: response.data.type,
        status: response.data.status || 'pending',
        timestamp: response.data.created_at,
        read: response.data.read,
        metadata: response.data.metadata
      };

      // Add to internal store
      this.notifications.unshift(notification);
      if (this.notifications.length > 50) {
        this.notifications = this.notifications.slice(0, 50);
      }

      // Notify all listeners
      this.listeners.forEach((callback, id) => {
        try {
          callback(notification);
        } catch (error) {
          console.error('Error calling listener:', error);
        }
      });

      return notification;
    } catch (error) {
      console.error('Error sending notification to API:', error);
      
      // Fallback to local storage if API fails
      const notification = {
        id: this.nextId++,
        title,
        message,
        type,
        timestamp: new Date().toISOString(),
        read: false,
        metadata
      };

      this.notifications.unshift(notification);
      this.saveNotifications();

      // Notify listeners
      this.listeners.forEach((callback, id) => {
        try {
          callback(notification);
        } catch (error) {
          console.error('Error calling listener:', error);
        }
      });

      return notification;
    }
  }

  // Get all notifications
  getNotifications() {
    return [...this.notifications];
  }

  // Mark notification as read
  async markAsRead(id) {
    const notification = this.notifications.find(n => n.id === id);
    if (notification) {
      try {
        await notificationApiService.markAsRead(id);
        notification.read = true;
      } catch (error) {
        console.error('Error marking notification as read in API:', error);
        // Fallback to local update
        notification.read = true;
        this.saveNotifications();
      }
    }
  }

  // Clear all notifications
  async clearAll() {
    try {
      await notificationApiService.clearAll();
      this.notifications = [];
    } catch (error) {
      console.error('Error clearing notifications from API:', error);
      // Fallback to local clear
      this.notifications = [];
      this.saveNotifications();
    }
  }

  // Load notifications from API
  async loadNotifications() {
    if (this.isLoading) return;
    
    this.isLoading = true;
    try {
      const response = await notificationApiService.getNotifications();
      
      // The API returns { success: true, data: [...], count: n }
      if (response.success && response.data) {
        // Map the API response to match frontend format
        this.notifications = response.data.map(notification => ({
          id: notification.id,
          title: notification.title,
          message: notification.message,
          type: notification.type,
          status: notification.status || 'pending',
          timestamp: notification.created_at || notification.timestamp,
          read: notification.read,
          metadata: notification.metadata
        }));
      } else {
        this.notifications = [];
      }
    } catch (error) {
      console.error('Error loading notifications from API:', error);
      
      // Fallback to localStorage
      try {
        const saved = localStorage.getItem('notificationService_notifications');
        if (saved) {
          const parsed = JSON.parse(saved);
          this.notifications = parsed.notifications || [];
          this.nextId = parsed.nextId || 1;
        }
      } catch (localError) {
        console.error('Error loading notifications from localStorage:', localError);
        this.notifications = [];
        this.nextId = 1;
      }
    } finally {
      this.isLoading = false;
    }
  }

  // Save notifications to localStorage
  saveNotifications() {
    try {
      const data = {
        notifications: this.notifications,
        nextId: this.nextId
      };
      localStorage.setItem('notificationService_notifications', JSON.stringify(data));
    } catch (error) {
      console.error('Error saving notifications to localStorage:', error);
    }
  }
}

// Create singleton instance
const notificationService = new NotificationService();

// Export the service
export default notificationService;

// Export helper function for easy use
export const addNotification = (title, message, type = 'info', metadata = null, permissionRequired = null) => {
  return notificationService.sendNotification(title, message, type, metadata, permissionRequired);
};






